import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import { LanguageProvider } from './context/LanguageContext';
import { CartProvider } from './context/CartContext';
import { ToastProvider } from './context/ToastContext';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import Layout from './components/Layout/Layout';
import LoadingSpinner from './components/LoadingSpinner/LoadingSpinner';

// Eager load critical pages (shown immediately)
import Home from './pages/Home/Home';
import Auth from './pages/Auth/Auth';

// Lazy load heavy pages (loaded on demand)
// Production'da deploy sonrası chunk hash değişince "Failed to fetch dynamically imported module" olabiliyor; retry ile sayfa yenilenir.
const isChunkError = (e) =>
  e?.message?.includes('Failed to fetch dynamically imported module') ||
  e?.message?.includes('Importing a module script failed') ||
  e?.message?.includes('Failed to load module script') ||
  e?.message?.includes('error loading dynamically imported module') ||
  e?.name === 'ChunkLoadError';

const lazyWithRetry = (importFn) =>
  lazy(async () => {
    const storageKey = 'chunk_reload_' + btoa(importFn.toString()).slice(0, 20);
    try {
      return await importFn();
    } catch (e) {
      if (isChunkError(e)) {
        const lastReload = sessionStorage.getItem(storageKey);
        if (!lastReload) {
          sessionStorage.setItem(storageKey, Date.now().toString());
          window.location.reload();
          return new Promise(() => {});
        }
        sessionStorage.removeItem(storageKey);
      }
      throw e;
    }
  });

const Marketplace = lazyWithRetry(() => import('./pages/Marketplace/Marketplace'));
const Sellers = lazyWithRetry(() => import('./pages/Sellers/Sellers'));
const SellerProfile = lazyWithRetry(() => import('./pages/SellerProfile/SellerProfile'));
const About = lazyWithRetry(() => import('./pages/About/About'));
const Contact = lazyWithRetry(() => import('./pages/Contact/Contact'));
const Profile = lazyWithRetry(() => import('./pages/Profile/Profile'));
const BecomeSeller = lazyWithRetry(() => import('./pages/BecomeSeller/BecomeSeller'));
const MyStore = lazyWithRetry(() => import('./pages/MyStore/MyStore'));
const SellerDashboard = lazyWithRetry(() => import('./pages/SellerDashboard/SellerDashboard'));
const ProductDetail = lazyWithRetry(() => import('./pages/ProductDetail/ProductDetail'));
const Cart = lazyWithRetry(() => import('./pages/Cart/Cart'));
const Checkout = lazyWithRetry(() => import('./pages/Checkout/Checkout'));
const Admin = lazyWithRetry(() => import('./pages/Admin/Admin'));
const PhotoTo3D = lazyWithRetry(() => import('./pages/PhotoTo3D/PhotoTo3D'));
const TestStorage = lazyWithRetry(() => import('./components/TestStorage/TestStorage'));
const ExampleValidatedForm = lazyWithRetry(() => import('./components/ExampleValidatedForm/ExampleValidatedForm'));
const KullanimKosullari = lazyWithRetry(() => import('./pages/Legal/KullanimKosullari'));
const MesafeliSatisSozlesmesi = lazyWithRetry(() => import('./pages/Legal/MesafeliSatisSozlesmesi'));
const KvkkAydinlatma = lazyWithRetry(() => import('./pages/Legal/KvkkAydinlatma'));
const GizlilikPolitikasi = lazyWithRetry(() => import('./pages/Legal/GizlilikPolitikasi'));
const SSS = lazyWithRetry(() => import('./pages/SSS/SSS'));
const Blog = lazyWithRetry(() => import('./pages/Blog/Blog'));
const BlogPost = lazyWithRetry(() => import('./pages/Blog/BlogPost'));
const Notifications = lazyWithRetry(() => import('./pages/Notifications/Notifications'));
const Messages = lazyWithRetry(() => import('./pages/Messages/Messages'));
const NotFound = lazyWithRetry(() => import('./pages/NotFound/NotFound'));
import { useAuth } from './context/AuthContext';
import { hasRole } from '@3dera/core/services/auth';
import './styles/variables.css';

/**
 * Route-level guard: redirects unauthenticated users to /auth.
 * requiredRole: single role string or array of roles (user needs at least one).
 */
const ProtectedRoute = ({ children, requiredRole, allowStoreMember }) => {
  const { isAuthenticated, userData, storeMembership } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  if (requiredRole) {
    const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    const userHasRole = userData?.roles && roles.some(r => hasRole(userData.roles, r));
    // Aktif mağaza üyeleri (manager/staff) de mağaza paneline erişebilir
    if (!userHasRole && !(allowStoreMember && storeMembership)) {
      return <Navigate to="/" replace />;
    }
  }

  return children;
};

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <LanguageProvider>
        <AuthProvider>
          <NotificationProvider>
          <ToastProvider>
          <CartProvider>
            <Router>
              <SpeedInsights />
              <Layout>
                <Suspense fallback={<LoadingSpinner />}>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/marketplace" element={<Marketplace />} />
                    <Route path="/sellers" element={<Sellers />} />
                    <Route path="/seller/:sellerId" element={<SellerProfile />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                    <Route path="/become-seller" element={<ProtectedRoute><BecomeSeller /></ProtectedRoute>} />
                    <Route path="/my-store" element={<ProtectedRoute requiredRole="seller" allowStoreMember><MyStore /></ProtectedRoute>} />
                    <Route path="/seller-dashboard" element={<ProtectedRoute requiredRole="seller" allowStoreMember><SellerDashboard /></ProtectedRoute>} />
                    <Route path="/photo-to-3d" element={<PhotoTo3D />} />
                    <Route path="/product/:productId" element={<ProductDetail />} />
                    <Route path="/cart" element={<Cart />} />
                    <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
                    <Route path="/admin" element={<ProtectedRoute requiredRole={['admin', 'moderator']}><Admin /></ProtectedRoute>} />
                    <Route path="/kullanim-kosullari" element={<KullanimKosullari />} />
                    <Route path="/mesafeli-satis-sozlesmesi" element={<MesafeliSatisSozlesmesi />} />
                    <Route path="/kvkk-aydinlatma" element={<KvkkAydinlatma />} />
                    <Route path="/gizlilik-politikasi" element={<GizlilikPolitikasi />} />
                    <Route path="/sss" element={<SSS />} />
                    <Route path="/blog" element={<Blog />} />
                    <Route path="/blog/:postId" element={<BlogPost />} />
                    <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
                    <Route path="/mesajlar" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
                    <Route path="/mesajlar/:conversationId" element={<ProtectedRoute><Messages /></ProtectedRoute>} />

                    {/* 404 Catch-all */}
                    <Route path="*" element={<NotFound />} />

                    {/* Test routes - only available in development */}
                    {import.meta.env.MODE === 'development' && (
                      <>
                        <Route path="/test-storage" element={<TestStorage />} />
                        <Route path="/example-validation" element={<ExampleValidatedForm />} />
                      </>
                    )}
                  </Routes>
                </Suspense>
              </Layout>
            </Router>
          </CartProvider>
          </ToastProvider>
          </NotificationProvider>
        </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
